import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card, CardContent } from './ui/card';
import AllocateTab from './sample-tracker/AllocateTab';
import SearchTab from './sample-tracker/SearchTab';
import PerformanceTab from './sample-tracker/PerformanceTab';

const TABS = [
  { key: 'allocate', label: 'Allocate Samples' },
  { key: 'overview', label: 'Overview' },
  { key: 'search', label: 'Search Design' },
  { key: 'performance', label: 'Performance' },
];

const OverviewTab = ({ overview, loading }) => {
  if (loading) return <p className="text-sm text-gray-400 p-6">Loading…</p>;
  if (!overview) return null;
  return (
    <div className="space-y-4" data-testid="sample-overview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Events" value={overview.total_events} />
        <StatCard label="Total Samples Allocated" value={overview.total_samples} />
        <StatCard label="By Type" value={overview.by_type.length} />
        <StatCard label="By Location" value={overview.by_location.length} />
      </div>
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-bold text-gray-800 mb-3">Samples per Event</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="px-3 py-2 font-semibold">Event</th>
                  <th className="px-3 py-2 font-semibold">Type</th>
                  <th className="px-3 py-2 font-semibold">Location</th>
                  <th className="px-3 py-2 font-semibold text-right">Samples</th>
                </tr>
              </thead>
              <tbody>
                {overview.events.map(e => (
                  <tr key={e.id} className="border-b border-gray-100" data-testid={`overview-row-${e.id}`}>
                    <td className="px-3 py-2 font-medium text-gray-800">
                      {e.name}{e.is_archived && <span className="ml-2 text-[10px] text-gray-400">(archived)</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{e.event_type || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{e.location || '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-700">{e.sample_count}</td>
                  </tr>
                ))}
                {overview.events.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400">No events yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GroupCard title="By Event Type" rows={overview.by_type} keyField="type" />
        <GroupCard title="By Location" rows={overview.by_location} keyField="location" />
      </div>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="rounded-lg border border-gray-200 p-3 bg-white">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
  </div>
);

const GroupCard = ({ title, rows, keyField }) => (
  <Card>
    <CardContent className="p-4">
      <h3 className="text-sm font-bold text-gray-800 mb-2">{title}</h3>
      <div className="space-y-1">
        {rows.map(r => (
          <div key={r[keyField]} className="flex justify-between text-sm py-1 border-b border-gray-50">
            <span className="text-gray-700">{r[keyField]} <span className="text-xs text-gray-400">({r.events} events)</span></span>
            <span className="font-semibold text-emerald-700">{r.samples}</span>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const SampleTracker = ({ user }) => {
  const [activeTab, setActiveTab] = useState('allocate');
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const loadEvents = useCallback(async () => {
    try {
      const res = await api.get('/events?include_archived=true');
      setEvents(res.data || []);
    } catch { toast.error('Failed to load events'); }
  }, []);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const res = await api.get('/sample-tracker/overview');
      setOverview(res.data);
    } catch { toast.error('Failed to load overview'); }
    finally { setOverviewLoading(false); }
  }, []);

  useEffect(() => {
    loadEvents();
    (async () => {
      try {
        const res = await api.get('/products');
        setProducts(res.data || []);
      } catch { /* products optional for some tabs */ }
    })();
  }, [loadEvents]);

  useEffect(() => {
    if (activeTab === 'overview') loadOverview();
  }, [activeTab, loadOverview]);

  return (
    <div className="p-6 space-y-5" data-testid="sample-tracker-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Sample Tracker</h1>
        <p className="text-sm text-gray-500">Track which designs were carried to which events, search a design's presence, and compare performance.</p>
      </div>

      <div className="flex flex-wrap gap-1 border-b">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === t.key ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid={`sample-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'allocate' && (
        <AllocateTab events={events} products={products} reloadEvents={loadEvents} />
      )}
      {activeTab === 'overview' && <OverviewTab overview={overview} loading={overviewLoading} />}
      {activeTab === 'search' && <SearchTab />}
      {activeTab === 'performance' && <PerformanceTab events={events} />}
    </div>
  );
};

export default SampleTracker;
